import React, { useEffect, useState } from 'react';
import { MemoryRouter } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { CircularProgress } from '@mui/material';
import { StyledCardSection, StepsRouter } from '@fnol/library/src/components';
import { getApplicationId, getIsSalesForce } from '@fnol/library/src/lib';
import { IntroMessagesSlider } from '@fnol/library/src/components/IntroMessagesSlider';
import { authClient } from '@fnol/library/src/api/auth';
import { submitTemporaryData } from '../../rpc/actions';
import { prepareDataForSubmit } from '../../lib/helpers';
import { constValues, insuranceGroup, legalPersonType, objectType, smsLinkPersonType } from '../../lib/consts';
import stepsComposer from '../../lib/stepsComposer';
import introMessagesStepsComposer from '../../lib/introMessagesStepsComposer';

const HouseApp = () => {
    const [loggedIn, setLoggedIn] = useState<boolean>(false);
    useEffect(() => {
        authClient.login('house', 242).then(() => setLoggedIn(true));
    }, []);

    return (
        <>
            {loggedIn ? (
                <Formik
                    enableReinitialize
                    initialValues={{
                        submittedSuccessfully: false,
                        submitError: '',
                        introMessagesOpen: true,
                        introMessagesSkipSteps: getIsSalesForce(getApplicationId()) ? [1, 2] : [],
                        skipSteps: [],
                        dateOfOccurance: '',
                        policyNumber: '',
                        policyNotFound: '',
                        publicUtility: '',
                        publicUtilities: [],
                        referenceNumber: '',
                        insuranceConfirmationID: '',
                        union: '',
                        unions: [],
                        insuranceMethod: '',
                        activePolicies: [], // policy detail screen
                        activePolicyNumber: '', // policy detail screen
                        activePolicy: null, // policy detail screen
                        groupInsuranceMethod: insuranceGroup.initialValue,
                        policyNumberAPI: '', // policy number returned by API can be different than search criteria
                        insuranceMethodInfo: '',
                        jbkProdukcija: false,

                        submitterType: legalPersonType.initialValue, // submitter detail screen
                        damagedObject: objectType.initialValue, // submitter detail screen
                        submitterIndividualPersonRole: '', // submitter detail screen
                        submitterLegalEntityPersonRole: '', // submitter detail screen

                        submitterIndividualName: '', // individual component for submitter
                        submitterIndividualLastName: '', // individual component for submitter
                        submitterIndividualUCN: '', // individual component for submitter
                        submitterIndividualCountry: constValues.countrySerbiaId, // individual component for submitter
                        submitterIndividualMyCountry: '', // individual component for submitter
                        submitterIndividualCity: '', // individual component for submitter
                        submitterIndividualCityInSerbia: '', // individual component for submitter
                        submitterIndividualAddress: '', // individual component for submitter
                        submitterIndividualAddressNumber: '', // individual component for submitter
                        submitterIndividualAddressApartment: '', // individual component for submitter
                        submitterIndividualPassportNumber: '', // individual component for submitter
                        submitterIndividualDateOfBirth: '', // individual component for submitter
                        submitterIndividualPhone: constValues.dialCodeSerbia, // individual component for submitter
                        submitterIndividualEmail: '', // individual component for submitter
                        submitterIndividualAccountNumber: '', // individual component for submitter

                        submitterLegalEntityCountry: constValues.countrySerbiaId, // legal/leasing compoment for submitter
                        submitterLegalEntityMyCountry: '', // legal/leasing compoment for submitter
                        submitterLegalEntityCity: '', // legal/leasing compoment for submitter
                        submitterLegalEntityCityInSerbia: '', // legal/leasing compoment for submitter
                        submitterLegalEntityAddress: '', // legal/leasing compoment for submitter
                        submitterLegalEntityAddressNumber: '', // legal/leasing compoment for submitter
                        submitterLegalEntityAddressApartment: '', // legal/leasing compoment for submitter
                        submitterLegalEntityPhone: constValues.dialCodeSerbia, // legal/leasing compoment for submitter
                        submitterLegalEntityContactPhone: constValues.dialCodeSerbia, // legal/leasing compoment for submitter
                        submitterLegalEntityName: '', // legal/leasing compoment for submitter
                        submitterLegalEntityID: '', // legal/leasing compoment for submitter
                        submitterLegalEntityVAT: '', // legal/leasing compoment for submitter
                        submitterLegalEntityEmail: '', // legal/leasing compoment for submitter
                        submitterLegalEntityContactName: '', // legal/leasing compoment for submitter
                        submitterLegalEntityContactLastName: '', // legal/leasing compoment for submitter
                        submitterLegalEntityContactEmail: '', // legal/leasing compoment for submitter

                        ownerIndividualName: '', // individual component for owner
                        ownerIndividualLastName: '', // individual component for owner
                        ownerIndividualUCN: '', // individual component for owner
                        ownerIndividualCountry: constValues.countrySerbiaId, // individual component for owner
                        ownerIndividualMyCountry: '', // individual component for owner
                        ownerIndividualCity: '', // individual component for owner
                        ownerIndividualCityInSerbia: '', // individual component for owner
                        ownerIndividualAddress: '', // individual component for owner
                        ownerIndividualAddressNumber: '', // individual component for owner
                        ownerIndividualAddressApartment: '', // individual component for owner
                        ownerIndividualPassportNumber: '', // individual component for owner
                        ownerIndividualDateOfBirth: '', // individual component for owner
                        ownerIndividualPhone: constValues.dialCodeSerbia, // individual component for owner
                        ownerIndividualEmail: '', // individual component for owner

                        ownerLegalEntityCountry: constValues.countrySerbiaId, // legal/leasing compoment for owner
                        ownerLegalEntityMyCountry: '', // legal/leasing compoment for owner
                        ownerLegalEntityCity: '', // legal/leasing compoment for owner
                        ownerLegalEntityCityInSerbia: '', // legal/leasing compoment for owner
                        ownerLegalEntityAddress: '', // legal/leasing compoment for owner
                        ownerLegalEntityAddressNumber: '', // legal/leasing compoment for owner
                        ownerLegalEntityAddressApartment: '', // legal/leasing compoment for owner
                        ownerLegalEntityPhone: constValues.dialCodeSerbia, // legal/leasing compoment for owner
                        ownerLegalEntityContactPhone: constValues.dialCodeSerbia, // legal/leasing compoment for owner
                        ownerLegalEntityName: '', // legal/leasing compoment for owner
                        ownerLegalEntityID: '', // legal/leasing compoment for owner
                        ownerLegalEntityVAT: '', // legal/leasing compoment for owner
                        ownerLegalEntityEmail: '', // legal/leasing compoment for owner
                        ownerLegalEntityContactName: '', // legal/leasing compoment for owner
                        ownerLegalEntityContactLastName: '', // legal/leasing compoment for owner
                        ownerLegalEntityContactEmail: '', // legal/leasing compoment for owner

                        streetNames: [],

                        loadingMessage: '',
                        objectCity: '',
                        objectStreet: '',
                        objectStreetNumber: '',
                        objectCode: '',
                        isShowSMSLink: false,
                        objectHasSmsLink: '', // Sms link selector for recording damage
                        objectSmsLinkType: smsLinkPersonType.initialValue, // Sms link selector for recording damage
                        objectSmsMobile: constValues.dialCodeSerbia, // Sms link selector for recording damage
                        cause: 0, // claim detail screen
                        causeName: '', // claim detail screen
                        damageTypes: [],
                        expectedAmount: '',
                        showSuggestedAmount: false,
                        suggestedAmount: '',
                        suggestedAmountAgreement: '',
                        damagedRooms: 0,
                        damageDescription: '',
                        uploadDocList: [],
                        roomTypesList: [],
                        validationError: '',
                        wallFinishTypesList: [],
                    }}
                    onSubmit={(values, { setFieldValue, setSubmitting }) => {
                        submitTemporaryData(prepareDataForSubmit(values))
                            .then((response) => {
                                setFieldValue('tempID', response.data);
                                setFieldValue('submittedSuccessfully', true);
                                setFieldValue('submitError', '');
                                setSubmitting(false);
                            })
                            .catch((error) => {
                                setFieldValue('submittedSuccessfully', false);
                                setFieldValue('submitError', error.response?.data);
                                setSubmitting(false);
                            });
                    }}
                >
                    {({ values }) => (
                        <>
                            <MemoryRouter>
                                <IntroMessagesSlider
                                    open={values.introMessagesOpen}
                                    steps={introMessagesStepsComposer()}
                                    skipSteps={values.introMessagesSkipSteps}
                                />
                            </MemoryRouter>
                            <Form>
                                <MemoryRouter>
                                    <StyledCardSection>
                                        <StepsRouter steps={stepsComposer()} skipSteps={values.skipSteps} />
                                    </StyledCardSection>
                                </MemoryRouter>
                            </Form>
                        </>
                    )}
                </Formik>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};

export default HouseApp;
